import React from "react";
import { parseApiDate } from "../Helper/dates";

/**
 * Simplified publication state of one content item. The preview environment publishes
 * itself after every save, so the only states an editor needs are: is this version live
 * in production (« En ligne »), does production lag behind (« Modifié »), is a publish
 * running (« Publication… ») or did it fail (« Échec »).
 */
export type ContentPublishStatus = "live" | "modified" | "publishing" | "failed";

/**
 * Honest derivation from whatever the payload carries: the production-deployed flag is
 * authoritative; when the payload also has timestamps, a save newer than the last
 * production deploy downgrades « En ligne » to « Modifié ».
 */
export const deriveContentStatus = (opts: {
	isProductionDeployed?: boolean;
	updatedAt?: string | null;
	productionDeployedAt?: string | null;
	publishing?: boolean;
	failed?: boolean;
}): ContentPublishStatus => {
	if (opts.publishing) return "publishing";
	if (opts.failed) return "failed";
	if (!opts.isProductionDeployed) return "modified";
	if (opts.updatedAt && opts.productionDeployedAt) {
		const updated = parseApiDate(opts.updatedAt);
		const deployed = parseApiDate(opts.productionDeployedAt);
		if (updated && deployed && updated.getTime() > deployed.getTime()) return "modified";
	}
	return "live";
};

const STYLES: Record<ContentPublishStatus, { cls: string; dot: string; label: string; title?: string }> = {
	live: {
		cls: "border-emerald-200 bg-emerald-50 text-emerald-700",
		dot: "bg-emerald-500",
		label: "En ligne"
	},
	modified: {
		cls: "border-amber-200 bg-amber-50 text-amber-700",
		dot: "bg-amber-500",
		label: "Modifié",
		title: "La preview se met à jour automatiquement"
	},
	publishing: {
		cls: "border-sky-200 bg-sky-50 text-sky-700",
		dot: "bg-sky-500 animate-pulse",
		label: "Publication…"
	},
	failed: {
		cls: "border-rose-200 bg-rose-50 text-rose-700",
		dot: "bg-rose-500",
		label: "Échec"
	}
};

/** The one status pill shown next to content everywhere in the admin. */
const ContentStatusPill: React.FC<{ status: ContentPublishStatus; className?: string }> = ({ status, className }) => {
	const s = STYLES[status];
	return (
		<span
			className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium ${s.cls} ${className ?? ""}`}
			title={s.title}
		>
			<span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} /> {s.label}
		</span>
	);
};

export default ContentStatusPill;
